// dev-portal/src/pages/marketplace/MarketplaceCategories.jsx

import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Package, Star, Download, ChevronRight, Search, Filter } from 'lucide-react';
import MarketplaceLayout from '../../layouts/MarketplaceLayout';
// Icon mapping - using simple emoji/text icons for simplicity
const getIconComponent = (iconName) => {
    const icons = {
        'briefcase': '💼',
        'trending-up': '📈',
        'code': '💻',
        'image': '🎨',
        'dollar-sign': '💰',
        'terminal': '⌨️',
        'heart': '❤️',
        'building': '🏢',
        'activity': '⚕️',
        'calendar': '📅',
        'mail': '📧',
        'users': '👥',
        'file-text': '📄',
        'zap': '⚡',
        'trello': '📋',
        'video': '🎥',
        'search': '🔍',
        'share-2': '🔗',
        'book-open': '📖',
        'smartphone': '📱',
        'layout': '🖼️',
        'music': '🎵',
        'palette': '🎨',
        'film': '🎬',
        'calculator': '🧮',
        'check-circle': '✅',
        'tool': '🔧',
        'headphones': '🎧',
        'message-circle': '💬',
        'message-square': '💬',
        'user-plus': '➕',
    };
    return icons[iconName] || '📦';
};


export default function MarketplaceCategories() {
    const [categories, setCategories] = useState([]);
    const [allApps, setAllApps] = useState([]);
    // State is now derived from URL + local object lookup
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubcategory, setSelectedSubcategory] = useState(null);

    const [apps, setApps] = useState([]);
    const [filteredApps, setFilteredApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingApps, setLoadingApps] = useState(false);

    // URL Params
    const [searchParams, setSearchParams] = useSearchParams();
    const categoryParam = searchParams.get('category');
    const subcategoryParam = searchParams.get('subcategory');

    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const navigate = useNavigate();

    // 1. Fetch Categories and All Apps once on mount
    useEffect(() => {
        fetchInitialData();
    }, []);

    // 2. React to URL param changes (or initial load completion) to set selected objects
    useEffect(() => {
        if (!loading && categories.length > 0) {
            syncStateWithUrl();
        }
    }, [categoryParam, subcategoryParam, loading, categories]);

    // 3. Fetch apps when selected category/subcategory changes
    useEffect(() => {
        if (selectedCategory) {
            fetchCategoryApps();
        } else {
            // No category selected: show all apps
            setApps(allApps);
        }
    }, [selectedCategory, selectedSubcategory, allApps]);

    // 4. Local filtering
    useEffect(() => {
        filterAndSortApps();
    }, [apps, searchTerm, sortBy]);

    const fetchInitialData = async () => {
        try {
            const [catsRes, appsRes] = await Promise.all([
                api.get('/api/categories'),
                api.get('/api/marketplace/apps')
            ]);

            setCategories(catsRes.data);
            setAllApps(appsRes.data || []);
            // setApps(appsRes.data || []); // Will be handled by useEffect #3
        } catch (error) {
            console.error('Failed to fetch initial marketplace data:', error);
        } finally {
            setLoading(false);
        }
    };

    const syncStateWithUrl = () => {
        if (categoryParam) {
            const category = categories.find(c => c.slug === categoryParam);
            if (category) {
                setSelectedCategory(category);

                if (subcategoryParam) {
                    const subcat = category.subcategories?.find(s => s.slug === subcategoryParam);
                    setSelectedSubcategory(subcat || null);
                } else {
                    setSelectedSubcategory(null);
                }
            } else {
                // Invalid category param, maybe clear URL?
                // For now, just ignore
                setSelectedCategory(null);
                setSelectedSubcategory(null);
            }
        } else {
            setSelectedCategory(null);
            setSelectedSubcategory(null);
        }
    };

    const fetchCategoryApps = async () => {
        if (!selectedCategory) return;

        setLoadingApps(true);
        try {
            const params = selectedSubcategory ? { subcategory: selectedSubcategory.slug } : {};
            const response = await api.get(`/api/marketplace/apps/category/${selectedCategory.slug}`, { params });
            setApps(response.data.apps || []);
        } catch (error) {
            console.error('Failed to fetch apps:', error);
            setApps([]);
        } finally {
            setLoadingApps(false);
        }
    };

    const filterAndSortApps = () => {
        let filtered = [...apps];

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(app =>
                app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                app.description?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Sort
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return a.name.localeCompare(b.name);
                case 'price_low':
                    return (a.price || 0) - (b.price || 0);
                case 'price_high':
                    return (b.price || 0) - (a.price || 0);
                case 'rating':
                    return (b.average_rating || 0) - (a.average_rating || 0);
                default:
                    return 0;
            }
        });

        setFilteredApps(filtered);
    };

    const handleCategoryClick = (category) => {
        // Update URL, triggering useEffect to update state
        setSearchParams({ category: category.slug });
        setSearchTerm('');
    };

    const handleAllAppsClick = () => {
        setSearchParams({});
        setSearchTerm('');
    };

    const handleSubcategoryClick = (subcategory) => {
        if (selectedSubcategory?.id === subcategory.id) {
            // Deselect -> Go back to just category
            setSearchParams({ category: selectedCategory.slug });
        } else {
            // Select subcategory
            setSearchParams({
                category: selectedCategory.slug,
                subcategory: subcategory.slug
            });
        }
        setSearchTerm('');
    };

    const handleAppClick = (app) => {
        const identifier = app.slug || app.id;
        navigate(`/marketplace/app/${identifier}`);
    };

    if (loading) {
        return (
            <MarketplaceLayout>
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </MarketplaceLayout>
        );
    }

    return (
        <MarketplaceLayout>
            <div className="bg-color-dark">
   <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2 bg-dark-head">Browse software</h1>
                    <p className="text-gray-600 bg-dark-text">Find the perfect apps for your needs</p>
                </div>

                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Left Sidebar - Shop by category */}
                    <div className="w-full lg:w-64 flex-shrink-0">
                        <div className="bg-white rounded-lg shadow p-4 sticky top-4 bg-dark-mode">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4 header-text-yellow">Shop by category</h2>
                            <nav className="space-y-1">
                                <div>
                                    <button
                                        onClick={handleAllAppsClick}
                                        className={`w-full text-left px-3 py-2 bg-yellow-mode-text rounded-md text-sm font-medium transition-colors ${!selectedCategory
                                            ? 'bg-blue-50 text-blue-700'
                                            : 'text-gray-700 hover:bg-gray-50 '
                                            }`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="flex-1">All apps</span>
                                            {allApps.length > 0 && (
                                                <span className="text-xs text-gray-500">{allApps.length}</span>
                                            )}
                                        </div>
                                    </button>
                                </div>
                                {categories.map((category) => (
                                    <div key={category.id}>
                                        <button
                                            onClick={() => handleCategoryClick(category)}
                                            className={`w-full text-left px-3 py-2 rounded-md bg-dark-mode-text text-sm font-medium transition-colors ${selectedCategory?.id === category.id
                                                ? 'bg-blue-50 text-blue-700'
                                                : 'text-gray-700 hover:bg-gray-50'
                                                }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <span className="flex-1">{category.name}</span>
                                                {category.app_count > 0 && (
                                                    <span className="text-xs text-gray-500 bg-dark-text">{category.app_count}</span>
                                                )}
                                            </div>
                                        </button>

                                        {/* Show subcategories when parent is selected */}
                                        {selectedCategory?.id === category.id && category.subcategories && category.subcategories.length > 0 && (
                                            <div className="ml-4 mt-1 space-y-1">
                                                {category.subcategories.map((subcat) => (
                                                    <button
                                                        key={subcat.id}
                                                        onClick={() => handleSubcategoryClick(subcat)}
                                                        className={`w-full text-left px-3 py-1.5 bg-dark-mode-text rounded-md text-xs transition-colors ${selectedSubcategory?.id === subcat.id
                                                            ? 'bg-blue-100 text-blue-700 font-medium'
                                                            : 'text-gray-600 hover:bg-gray-50 '
                                                            }`}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <span>{subcat.name}</span>
                                                            <span className="text-xs text-gray-400 bg-dark-text">{subcat.app_count}</span>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </nav>
                        </div>
                    </div>

                    {/* Main Content Area - Apps Grid */}
                    <div className="flex-1">
                        {selectedCategory && (
                            <>
                                {/* Category Header */}
                                <div className="mb-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <div>
                                            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 bg-dark-head">
                                                <span className="text-3xl">{getIconComponent(selectedCategory.icon)}</span>
                                                {selectedSubcategory ? selectedSubcategory.name : selectedCategory.name}
                                            </h2>
                                            <p className="text-gray-600 mt-1 bg-dark-text">
                                                {selectedSubcategory ? selectedSubcategory.description : selectedCategory.description}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Search and Filter Bar */}
                                <div className="p-4 mb-6">
                                    <div className="flex flex-col md:flex-row gap-4">
                                        <div className="flex-1 relative">
                                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                            <input
                                                type="text"
                                                placeholder={`Search ${selectedSubcategory ? selectedSubcategory.name : selectedCategory.name} apps...`}
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="w-full pl-10 pr-4 py-3 border rounded-lg bg-search-dark focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <select
                                                value={sortBy}
                                                onChange={(e) => setSortBy(e.target.value)}
                                                className="px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-search-dark"
                                            >
                                                <option value="name">Sort by Name</option>
                                                <option value="price_low">Price: Low to High</option>
                                                <option value="price_high">Price: High to Low</option>
                                                <option value="rating">Highest Rated</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Results Count */}
                                <div className="mb-4">
                                    <p className="text-gray-600 bg-dark-text">
                                        Showing {filteredApps.length} {filteredApps.length === 1 ? 'app' : 'apps'}
                                    </p>
                                </div>

                                {/* Apps Grid */}
                                {loadingApps ? (
                                    <div className="flex justify-center items-center h-64">
                                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                                    </div>
                                ) : filteredApps.length === 0 ? (
                                    <div className="text-center py-12  rounded-lg">
                                        <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                        <p className="text-gray-600 bg-dark-text">No apps found matching your criteria</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {filteredApps.map(app => (
                                            <AppCard key={app.id} app={app} onClick={() => handleAppClick(app)} />
                                        ))}
                                    </div>
                                )}
                            </>
                        )}

                        {!selectedCategory && categories.length === 0 && (
                            <div className="text-center py-12">
                                <p className="text-gray-600 bg-dark-text">No categories available yet</p>
                            </div>
                        )}

                        {!selectedCategory && categories.length > 0 && (
                            <div className="rounded-lg shadow-sm p-4 mb-6">
                                <h2 className="text-2xl font-bold text-gray-900 mb-2 bg-dark-head">All apps</h2>
                                <p className="text-gray-600 bg-dark-text bg-dark-text">Browse every publicly visible app. Use categories on the left to narrow down, or search below.</p>
                            </div>
                        )}

                        {!selectedCategory && (
                            <>
                                <div className=" shadow-sm p-4 mb-6">
                                    <div className="flex flex-col md:flex-row gap-4">
                                        <div className="flex-1 relative">
                                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                            <input
                                                type="text"
                                                placeholder="Search all apps..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="w-full pl-10 pr-4 py-3 border rounded-lg bg-search-dark focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div className="flex items-center gap-2">
                                        
                                            <select
                                                value={sortBy}
                                                onChange={(e) => setSortBy(e.target.value)}
                                                className="px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-search-dark"
                                            >
                                                <option value="name">Sort by Name</option>
                                                <option value="price_low">Price: Low to High</option>
                                                <option value="price_high">Price: High to Low</option>
                                                <option value="rating">Highest Rated</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <p className="text-gray-600 bg-dark-text">
                                        Showing {filteredApps.length} {filteredApps.length === 1 ? 'app' : 'apps'}
                                    </p>
                                </div>

                                {loadingApps ? (
                                    <div className="flex justify-center items-center h-64">
                                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                                    </div>
                                ) : filteredApps.length === 0 ? (
                                    <div className="text-center py-12 rounded-lg">
                                        <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                        <p className="text-gray-600 bg-dark-text">No apps found matching your criteria</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {filteredApps.map(app => (
                                            <AppCard key={app.id} app={app} onClick={() => handleAppClick(app)} />
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
            </div>
         
        </MarketplaceLayout>
    );
}

// App Card Component
const AppCard = ({ app, onClick }) => {
    return (
        <div
            onClick={onClick}
            className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-all  bg-box-card-dark cursor-pointer group border border-gray-200 hover:border-blue-300"
        >
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-3">
                    {app.icon_url ? (
                        <img src={app.icon_url} alt={app.name} className="w-14 h-14 rounded-lg" />
                    ) : (
                        <div className="w-14 h-14 bg-gray-200 rounded-lg flex items-center justify-center">
                            <Package className="w-7 h-7 text-gray-500" />
                        </div>
                    )}

                    {/* App Type Badge */}
                    {app.type && (
                        <span className={`px-2 py-1 rounded price-tag-bgyellow text-xs font-medium ${app.type === 'internal'
                            ? 'bg-gray-100 text-gray-700'
                            : app.type === 'partner'
                                ? 'bg-purple-100 text-purple-700'
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                            {app.type.charAt(0).toUpperCase() + app.type.slice(1)}
                        </span>
                    )}
                </div>
          
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors bg-box-card-dark-head">{app.name}</h3>

            <p className="text-gray-600 text-sm mb-4 line-clamp-2 bg-box-card-dark-head-text">
                {app.short_description || app.description}
            </p>

            <div className="flex items-center justify-between">
                <div className="flex items-center text-sm text-gray-500 bg-box-card-dark-head-rating">
                    {(app.average_rating && app.average_rating > 0) ? (
                        <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-yellow-500 fill-current" />
                            <span>{app.average_rating.toFixed(1)}</span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-1 text-gray-400">
                            <Star className="w-4 h-4" />
                            <span className="text-xs">No ratings</span>
                        </div>
                    )}
                </div>

                <div className="font-semibold text-gray-900 bg-box-card-dark-head-price">
                    {app.pricing_model === 'free' ? (
                        <span className="text-green-600">Free</span>
                    ) : (
                        <span>${app.price}</span>
                    )}
                </div>
            </div>
        </div>
    );
};